import { useTranslation } from 'react-i18next';
import { KpiCard, KpiGrid } from '@/shared/components/dashboard/KpiCard';
import type { PlatformUsageSummary } from '../types';

interface ActivitySummaryCardsProps {
  summary: PlatformUsageSummary;
}

export function ActivitySummaryCards({ summary }: ActivitySummaryCardsProps) {
  const { t } = useTranslation();

  const pageViews = summary.eventsByType.page_view ?? 0;
  const moduleOpens = summary.eventsByType.module_opened ?? 0;
  const actionsPerformed = summary.eventsByType.action_performed ?? 0;
  const uniqueProducts = summary.byProduct.length;

  return (
    <section className="space-y-3" aria-labelledby="platform-usage-summary-heading">
      <h2 id="platform-usage-summary-heading" className="text-base font-semibold text-foreground">
        {t('platformUsage.summary.title', { minutes: summary.minutes })}
      </h2>
      <KpiGrid className="xl:grid-cols-3">
        <KpiCard
          title={t('platformUsage.summary.totalEvents')}
          value={String(summary.totalEvents)}
          tone="blue"
        />
        <KpiCard
          title={t('platformUsage.summary.pageViews')}
          value={String(pageViews)}
          tone="cyan"
        />
        <KpiCard
          title={t('platformUsage.summary.moduleOpens')}
          value={String(moduleOpens)}
          tone="violet"
        />
        <KpiCard
          title={t('platformUsage.summary.actionsPerformed')}
          value={String(actionsPerformed)}
          tone="amber"
        />
        <KpiCard
          title={t('platformUsage.summary.activeSessions')}
          value={String(summary.activeSessions)}
          tone="green"
        />
        <KpiCard
          title={t('platformUsage.summary.uniqueProducts')}
          value={String(uniqueProducts)}
          tone="rose"
        />
      </KpiGrid>
    </section>
  );
}

export function ActivitySummarySkeleton() {
  return (
    <div className="space-y-3">
      <div className="h-5 w-48 rounded bg-muted animate-pulse" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="rounded-xl border border-border bg-card p-5 animate-pulse">
            <div className="h-3 w-24 rounded bg-muted" />
            <div className="mt-3 h-8 w-14 rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}
